import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Overridable so a staging deployment can point somewhere else — or set the
// variable empty to switch analytics off entirely — without its traffic
// landing in the production property.
const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID ?? "G-4T9363V6N2";

/**
 * Loads GA4 (gtag.js) at runtime and sends a page_view on every route change
 * (and on initial mount).
 *
 * The tag is injected here rather than written into index.html. That began as
 * a Base44 workaround — Publish regenerated index.html and stripped custom
 * <head> scripts — and is kept because it kept the measurement id out of the
 * static shell and lets the id come from the environment.
 *
 * The tag is configured with `send_page_view: false`, so this component is the
 * single source of page_view events — including the first load — which keeps
 * SPA client-side navigations (React Router) tracked without double-counting.
 * Renders nothing.
 */

let gtagInitialized = false;

function initGtag() {
  if (gtagInitialized || typeof window === "undefined" || !document.head) return;
  if (!GA_MEASUREMENT_ID) return;
  gtagInitialized = true;

  window.dataLayer = window.dataLayer || [];
  // gtag() queues calls onto dataLayer, so config/page_view calls made before
  // the external script finishes loading are replayed once it arrives.
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

export default function Analytics() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    initGtag();
  }, []);

  useEffect(() => {
    window.gtag?.("event", "page_view", {
      page_path: pathname + search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, search]);

  return null;
}
